import type { CreateUserPayload } from '@/api/usersApi'

/**
 * The body of "Add user", built in one tested place.
 *
 * Access is the half that used to go missing. The dialog only ever sent the
 * account fields, so `tenants` was always absent and every account was created
 * belonging to no tenant with a role nowhere — it could sign in and do nothing,
 * and nothing on screen said so. Building the payload here means the membership
 * is covered by a test rather than by a hand-checked object literal in a `.tsx`
 * file, which this repo's vitest config cannot run at all.
 *
 * Memberships are a LIST because one person can belong to several hospitals —
 * `TenantMembership` is keyed `[userId, tenantId]` and carries its own role, so
 * the same account can be an admin at one tenant and a supervisor at another.
 * The dialog used to offer a single tenant, which forced the operator to create
 * the account and then reopen Tenant access to add the rest.
 *
 * `roleId` is separable from `tenantId` because a membership with no role is a
 * real state — a member of the tenant who holds no permissions there yet — and
 * the backend models it as `roleId: null`. A tenant with no role is therefore
 * sent; a role with no tenant is not, because a role means nothing outside one.
 */
export function buildCreateUserPayload(input: {
  email: string
  firstName: string
  lastName: string
  temporaryPassword: string
  memberships: { tenantId: string; roleId?: string }[]
}): CreateUserPayload {
  // Deduped by tenant id: `[userId, tenantId]` is unique server-side, so a
  // repeated tenant would fail the whole create AFTER the Cognito identity was
  // already provisioned. First occurrence wins.
  const byTenant = new Map<string, { tenantId: string; roleId?: string }>()
  for (const m of input.memberships) {
    const tenantId = m.tenantId.trim()
    if (!tenantId || byTenant.has(tenantId)) continue
    const roleId = m.roleId?.trim()
    byTenant.set(tenantId, { tenantId, ...(roleId ? { roleId } : {}) })
  }
  const tenants = [...byTenant.values()]

  return {
    email: input.email.trim(),
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    temporaryPassword: input.temporaryPassword,
    ...(tenants.length > 0 ? { tenants } : {}),
  }
}
